// Percorso: \src\components\UniformConfig.jsx
//
// LA MODALE DI CONFIGURAZIONE DEL CATALOGO DIVISE (UNIFORMS-UI/Fetta B,
// 19/9/2026 — riscritta). Tre parti, dall'alto: la LISTA DEI CAPI (aggiungi,
// rinomina, riordina, spegni/riaccendi, taglia sì/no), le spunte «By Role» e
// le spunte «By Team — only for Players», con le colonne che nascono dai capi
// ATTIVI della lista. Nessun «Delete»: un capo si spegne (conferma A del
// committente), sparisce dalla vista e resta nel dato con le sue consegne.
//
// ⚠️ SEGUE L'ANNO GUARDATO, e lo dice nel titolo (UNIFORMS-UI/Fetta C-config,
// decisione del committente): legge e salva il catalogo dell'anno che la
// pagina sta guardando («Season Viewed»), dichiarato al server con lo stesso
// `?season=` nelle TRE chiamate — la lettura all'apertura, il `PUT` del Save e
// la rilettura dopo il Save (server.js, cappello di `riferimentoCatalogoDivise`:
// la regola nei due versi). L'anno arriva dalla pagina come prop (`stagione`,
// il nome che Team Assignment dà alla sua modale gemella; UNIF3-1 §2.5
// opzione i): questa modale non legge `useAuth` da sé, e la pagina la monta
// con `key={stagione}`, così un cambio d'anno la rimonta da zero.
//
// ⚠️ NESSUNA SCRITTURA SENZA «Save». Anche la «copia dall'anno precedente» è
// solo una PRECOMPILAZIONE della lista a schermo: il salvataggio parte al
// «Save», ed è lo stesso PUT di sempre col contenuto `items` dell'anno prima.
//
// ⚠️ IL RIPIEGO agli otto capi storici (`items` null) vive in
// `utils/uniforms.js` (`capiDelCatalogo`, `capiPredefiniti`): qui si chiama,
// non si ricopia.
//
// Veste: guscio del kit (`qpsc-modal`, doc 07 §8), deroga di larghezza sul
// calco di Payments (`.qpsc-modal.uc-modal`, UniformConfig.css), lista sul
// calco di Manage Teams (TeamAssignment.jsx, `.ta-team-form`/`.ta-team-row`),
// spunte `qpsc-check` (doc 09 §4), messaggi IN MODALE sul calco di Manage
// Teams (`.ta-alert`/`.ta-info`) — nessun `alert()` di sistema.
import React, { useEffect, useRef, useState } from "react";
import { api } from "../api/api";
import { canonicalRole } from "../utils/roleUtils";
// SEDE UNICA delle divise (UNIFORMS/D7, poi Fetta B): chi riceve divise per
// ruolo, il ripiego della lista dei capi e le due letture d'ordine.
import {
  RUOLI_CON_DIVISE,
  capiDelCatalogo,
  capiPredefiniti,
  capiOrdinati,
  capiAttivi
} from "../utils/uniforms";
// ⚠️ L'ORDINE DEI DUE FOGLI CONTA (UsersList, UniformDistribution):
// il kit prima, il foglio della modale dopo, così a parità di peso vince
// quello della modale.
import "./AdminTools.css";
import "./UniformConfig.css";

// Il catalogo dell'annata che non esiste ancora: si parte da vuoto, ed è
// esattamente il caso per cui questa modale serve (vedi `caricaCatalogo`).
const CATALOGO_VUOTO = { roles: {}, teams: {} };

// TESTI A SCHERMO (inglese, CLAUDE.md §3.7). I due del caricamento sono
// quelli della pagina, verbatim: stesso guasto, stesse parole.
const MSG_STAGIONE = "Could not read the club season. Please try again in a moment.";
const MSG_CONFIG = "Could not load the uniform catalogue. Please try again in a moment.";
const MSG_ATTESA = "Loading uniform configuration…";
const MSG_SALVATO = "Saved.";
const MSG_SALVATAGGIO = "Could not save the configuration. Please try again.";
const MSG_RICARICA =
  "Saved, but the catalogue could not be reloaded. Close and reopen this window before saving again.";
const MSG_NUOVI =
  "New items appear in the tables below after you save: their code is assigned on save.";

// Il testo che il server allega a un rifiuto (`{ error }`), letto dal corpo
// grezzo che `api.js` appende all'errore (`err.body`). Calco di
// `handleDelete` in Manage Teams (TeamAssignment.jsx): il 409 dice PERCHÉ,
// e quel perché deve arrivare a chi ha premuto — non un «Save failed» uguale
// per tutto (coda 5 del 4/9, UNIF3-1 §2.5).
const motivoDelServer = (err) => {
  try {
    const corpo = JSON.parse(err?.body);
    return typeof corpo?.error === "string" ? corpo.error : "";
  } catch (e) {
    return "";
  }
};

// La riga di lista di un capo: i cinque campi del catalogo più `chiave`, il
// nome con cui React distingue la riga. Per un capo che ha già l'id la chiave
// È l'id; per un capo NUOVO (id `null`, lo assegna il server) è una chiave
// locale provvisoria, che NON parte mai col salvataggio.
const rigaDaCapo = (capo) => ({
  id: capo.id,
  label: String(capo.label ?? capo.id ?? ""),
  hasSize: capo.hasSize !== false,
  active: capo.active !== false,
  chiave: capo.id
});

// Il capo come parte al server: i soli cinque campi, `order` = posizione in
// lista, e l'`id` SOLO se c'è — un capo nuovo viaggia senza, e il server
// gliene assegna uno opaco (`vaglioCapiDivisa`, server.js).
const capoDaRiga = (riga, indice) => ({
  ...(riga.id ? { id: riga.id } : {}),
  label: riga.label.trim(),
  hasSize: Boolean(riga.hasSize),
  active: Boolean(riga.active),
  order: indice
});

const UniformConfig = ({ onClose, stagione }) => {
  const [config, setConfig] = useState(null);
  // La lista dei capi come si vede a schermo, nell'ORDINE di visualizzazione:
  // `order` si ricava dalla posizione al momento del salvataggio.
  const [capi, setCapi] = useState([]);
  const [nuovaLabel, setNuovaLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [errore, setErrore] = useState(null);
  // L'esito dell'ultimo gesto, DENTRO la modale (calco `.ta-alert`/`.ta-info`
  // di Manage Teams): `{ tipo: "ok" | "errore", testo }`, o `null`.
  const [messaggio, setMessaggio] = useState(null);
  // La «copia dall'anno precedente»: gli `items` dell'anno prima, se l'anno
  // guardato non ha ancora una lista e l'anno prima ne ha una; `null` se
  // non c'è nulla da offrire. Finché la domanda è aperta la lista non si
  // mostra: non c'è ancora una lista da mostrare.
  const [offertaCopia, setOffertaCopia] = useState(null);
  const contatoreNuovi = useRef(0);

  const annoLeggibile = Number.isInteger(stagione);
  const annoPrecedente = annoLeggibile ? stagione - 1 : null;

  // IL PERCORSO DEL CATALOGO DELL'ANNO GUARDATO, calcolato UNA volta dalla prop
  // e usato dalle tre chiamate (lettura, Save, rilettura): così non possono
  // divergere. Calco della lettura dell'anno prima qui sotto e della pagina
  // (UniformDistribution.jsx, `fetchConfig`): se l'anno non è un intero il
  // percorso ripiega a nudo, e il server risponde con l'operativo.
  const percorso = annoLeggibile
    ? `/uniform-config?season=${encodeURIComponent(stagione)}`
    : "/uniform-config";

  useEffect(() => {
    caricaCatalogo();
    // L'anno è la prop `stagione`, e la pagina monta la modale con
    // `key={stagione}`: a un cambio d'anno la modale si rimonta da zero, quindi
    // qui basta il montaggio.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * IL CARICAMENTO. Due letture al più:
   *   1. `GET /uniform-config?season={anno guardato}` → il catalogo dell'anno
   *      GUARDATO, che è quello che il PUT scrive. 404 = catalogo non ancora
   *      nato: si parte da vuoto (non è un errore, è il caso in cui la modale
   *      serve di più).
   *   2. Se quel catalogo non porta `items` (o non esiste) e l'anno prima si
   *      conosce: `GET /uniform-config?season={anno-1}` per OFFRIRE la copia
   *      della lista dell'anno prima. Qualunque esito non buono di questa
   *      seconda lettura (404, 409, rete) NON blocca la modale: non c'è nulla
   *      da copiare, e si parte dal ripiego.
   * Con `items` presente la lista è quella, ordinata per `order`.
   */
  const caricaCatalogo = async () => {
    let dati;

    try {
      dati = await api.get(percorso);
    } catch (err) {
      // ⚠️ I DUE CODICI, distinti sul solo `err.status` che `api.js` allega a
      // ogni risposta non ok. 404 = il catalogo dell'annata non esiste ancora.
      if (err?.status === 404) {
        dati = { ...CATALOGO_VUOTO, items: null };
      } else {
        // 409 = la cassettina della stagione non si legge: guasto vero, e
        // senza l'anno non si saprebbe nemmeno quale catalogo si sta scrivendo.
        console.error("Failed to load uniform config", err);
        setConfig(null);
        setErrore(err?.status === 409 ? MSG_STAGIONE : MSG_CONFIG);
        return;
      }
    }

    const roles = dati?.roles && typeof dati.roles === "object" ? dati.roles : {};
    const teams = dati?.teams && typeof dati.teams === "object" ? dati.teams : {};
    const items = Array.isArray(dati?.items) ? dati.items : null;

    setConfig({ roles, teams });
    setErrore(null);

    if (items !== null) {
      setCapi(capiOrdinati(items).map(rigaDaCapo));
      setOffertaCopia(null);
      return;
    }

    // Nessuna lista salvata per l'anno guardato: c'è quella dell'anno prima?
    const precedenti = await itemsDellAnnoPrecedente();

    if (precedenti && precedenti.length > 0) {
      setOffertaCopia(capiOrdinati(precedenti));
      setCapi([]);
      return;
    }

    setOffertaCopia(null);
    setCapi(capiDelCatalogo(null).map(rigaDaCapo));
  };

  const itemsDellAnnoPrecedente = async () => {
    if (annoPrecedente === null) return null;

    try {
      const dati = await api.get(
        `/uniform-config?season=${encodeURIComponent(annoPrecedente)}`
      );
      return Array.isArray(dati?.items) ? dati.items : null;
    } catch (err) {
      // Nessuna lista da offrire, qualunque sia il motivo: si parte dal
      // ripiego. Non è un guasto della modale.
      return null;
    }
  };

  // «Copy»: la lista dell'anno prima diventa la lista a schermo — con gli
  // id dell'anno prima CONSERVATI (sono già opachi e validi). Non salva:
  // l'admin poi preme «Save».
  const copiaDallAnnoPrecedente = () => {
    setCapi((offertaCopia || []).map(rigaDaCapo));
    setOffertaCopia(null);
  };

  // «Use the standard list»: si parte dal ripiego, gli otto capi storici.
  const partiDalRipiego = () => {
    setCapi(capiPredefiniti().map(rigaDaCapo));
    setOffertaCopia(null);
  };

  // ------------------------------------------------------------------
  // I GESTI SULLA LISTA — ogni gesto produce un array NUOVO (mai `push` sullo
  // stato precedente: la copia superficiale di prima mutava lo stato in
  // loco, UNIF3-1 §2.5).
  // ------------------------------------------------------------------
  const aggiornaCapo = (chiave, campi) =>
    setCapi((prev) =>
      prev.map((riga) => (riga.chiave === chiave ? { ...riga, ...campi } : riga))
    );

  // Rinominare cambia SOLO la label: l'id non cambia mai dopo la nascita.
  const rinomina = (chiave, label) => aggiornaCapo(chiave, { label });
  const impostaTaglia = (chiave, hasSize) => aggiornaCapo(chiave, { hasSize });
  const impostaAttivo = (chiave, active) => aggiornaCapo(chiave, { active });

  // Riordino: la riga scambia di posto con la vicina. Nessun precedente di
  // riordino nel kit (UNIF3-1 §2.6, ⚪): due pulsanti fantasma ▲/▼ (§5.3),
  // spenti ai due capi della lista.
  const sposta = (indice, passo) =>
    setCapi((prev) => {
      const destinazione = indice + passo;
      if (destinazione < 0 || destinazione >= prev.length) return prev;
      const copia = prev.slice();
      [copia[indice], copia[destinazione]] = [copia[destinazione], copia[indice]];
      return copia;
    });

  // «Add item»: un capo NUOVO in coda — label digitata, taglia sì, acceso,
  // NESSUN id (lo mette il server). La chiave locale serve solo a React.
  const aggiungi = () => {
    const label = nuovaLabel.trim();
    if (!label) return;

    contatoreNuovi.current += 1;

    setCapi((prev) => [
      ...prev,
      {
        id: null,
        label,
        hasSize: true,
        active: true,
        chiave: `nuovo-${contatoreNuovi.current}`
      }
    ]);
    setNuovaLabel("");
  };

  // La spunta di una cella: scrive/toglie l'ID del capo nell'array della
  // chiave (`roles[COACH]`, `teams[<squadra>]`). ⚠️ DIFESA contro una chiave
  // che non porti un array (`null`, stringa, oggetto — il backend non lo
  // impedisce): si legge come elenco vuoto, e il primo click crea l'elenco.
  // Aggiornamento IMMUTABILE: la sezione e l'array sono oggetti nuovi.
  const toggleValue = (section, key, itemId) => {
    setConfig((prev) => {
      const sezione = prev?.[section] && typeof prev[section] === "object" ? prev[section] : {};
      const attuale = Array.isArray(sezione[key]) ? sezione[key] : [];
      const aggiornato = attuale.includes(itemId)
        ? attuale.filter((v) => v !== itemId)
        : [...attuale, itemId];

      return { ...prev, [section]: { ...sezione, [key]: aggiornato } };
    });
  };

  /*
   * IL SALVATAGGIO: un solo `PUT /uniform-config?season={anno guardato}` con
   * `roles`, `teams` e `items` (i capi nell'ordine a schermo, `order` =
   * posizione; i nuovi senza id). L'anno viaggia in QUERY, non nel corpo
   * (server.js, PUT /uniform-config). Dopo un esito buono si RILEGGE il
   * catalogo DELLO STESSO ANNO: i capi nuovi tornano con l'id assegnato dal
   * server — senza la rilettura, un secondo «Save» li rimanderebbe ancora
   * senza id e il server ne creerebbe dei doppioni. Il 409 (un capo mancante:
   * «si spegne, non si cancella») e il 400 (forma, o «Invalid season») portano
   * il messaggio DEL SERVER; ogni altro esito il messaggio generico. Nessun
   * `alert()`.
   */
  const handleSave = async () => {
    if (!config || offertaCopia) return;

    setSaving(true);
    setMessaggio(null);

    try {
      await api.put(percorso, {
        roles: config.roles,
        teams: config.teams,
        items: capi.map(capoDaRiga)
      });
    } catch (err) {
      const motivo = motivoDelServer(err);
      setMessaggio({
        tipo: "errore",
        testo:
          (err?.status === 409 || err?.status === 400) && motivo
            ? motivo
            : MSG_SALVATAGGIO
      });
      setSaving(false);
      return;
    }

    try {
      const dati = await api.get(percorso);
      const items = Array.isArray(dati?.items) ? dati.items : null;
      setConfig({
        roles: dati?.roles && typeof dati.roles === "object" ? dati.roles : {},
        teams: dati?.teams && typeof dati.teams === "object" ? dati.teams : {}
      });
      setCapi(capiOrdinati(capiDelCatalogo(items)).map(rigaDaCapo));
      setMessaggio({ tipo: "ok", testo: MSG_SALVATO });
    } catch (err) {
      // Salvato ma non riletto: si dice, e si chiude la porta a un secondo
      // Save alla cieca (vedi il cappello).
      setErrore(MSG_RICARICA);
    } finally {
      setSaving(false);
    }
  };

  // Le righe della tabella «By Role»: l'unione fra i ruoli che ricevono divise
  // (sede unica) e le chiavi già salvate, nella loro GRAFIA ORIGINALE, mai
  // tradotte (è la chiave che il Save scrive: access.js). PLAYER non
  // compare: le divise del giocatore dipendono dalla SQUADRA.
  const righeRuoli = Array.from(
    new Set([...RUOLI_CON_DIVISE, ...Object.keys(config?.roles || {})])
  ).filter((role) => role && canonicalRole(role) !== "PLAYER");

  // Le COLONNE delle due tabelle di spunta: i soli capi ATTIVI, nell'ordine a
  // schermo. Un capo spento non è colonna (non si spuntano capi spenti); le
  // sue spunte già salvate restano negli array, intoccate.
  // ⚠️ Un capo NUOVO non è colonna finché non si salva: la spunta scrive l'ID
  // negli array, e l'id lo assegna il server al Save (`capiAttivi` scarta le
  // voci senza id). La nota `MSG_NUOVI` lo dice a chi ha appena aggiunto.
  const colonne = capiAttivi(
    capi.map((riga, indice) => ({ ...riga, order: indice }))
  );
  const conNuoviNonSalvati = capi.some((riga) => !riga.id);

  const titolo = annoLeggibile
    ? `Uniform Configuration — ${stagione}`
    : "Uniform Configuration";

  // ⚠️ L'ERRORE NON È UN'ATTESA INFINITA: il guasto si dice, e il «Close»
  // resta (stesso guscio, stessa azione).
  if (errore) {
    return (
      <div className="qpsc-modal-overlay">
        <div className="qpsc-modal uc-modal">
          <h2 className="qpsc-modal__title">{titolo}</h2>
          <p className="uc-alert">{errore}</p>
          <div className="qpsc-modal__actions">
            <button type="button" className="qpsc-ghost-btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="qpsc-modal-overlay">
        <div className="qpsc-modal uc-modal">
          <h2 className="qpsc-modal__title">{titolo}</h2>
          <p className="qpsc-modal__note uc-centered">{MSG_ATTESA}</p>
        </div>
      </div>
    );
  }

  // Una tabella di spunte — «By Role» o «By Team»: prima colonna la chiave,
  // poi una spunta per capo attivo. La spunta è la forma di casa (`qpsc-check`,
  // doc 09 §4): il quadrato lo disegna lo <span>, l'<input> nativo resta il
  // comando. Sotto i 768px la tabella scorre in orizzontale dentro il suo
  // contenitore con la prima colonna fissa (calco Coach Attendance, doc 07
  // §8.1 — deroga alle card impilate, dichiarata nel referto).
  const tabellaSpunte = (section, righe, intestazione, notaRiga) => (
    <div className="uc-table-wrap">
      <table className="uc-table">
        <thead>
          <tr>
            <th scope="col">{intestazione}</th>
            {colonne.map((capo) => (
              <th key={capo.id} scope="col">{capo.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {righe.map((key) => (
            <tr key={key}>
              <th scope="row">
                {key}
                {notaRiga ? notaRiga(key) : null}
              </th>
              {colonne.map((capo) => {
                const valori = config[section]?.[key];
                const spuntato = Array.isArray(valori) && valori.includes(capo.id);
                return (
                  <td key={capo.id}>
                    <label className="qpsc-check uc-check">
                      <input
                        className="qpsc-check__input"
                        type="checkbox"
                        aria-label={`${key} — ${capo.label}`}
                        checked={spuntato}
                        onChange={() => toggleValue(section, key, capo.id)}
                      />
                      <span className="qpsc-check__box" aria-hidden="true" />
                    </label>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="qpsc-modal-overlay">
      {/* `uc-modal` è la classe MODIFICATORE di questa sola finestra: porta la
          deroga di larghezza sopra i 768px (UniformConfig.css), come
          `qpsc-pay-modal` per Payments e `ta-manage-modal` per Manage Teams. */}
      <div className="qpsc-modal uc-modal">
        {/* ⚠️ L'ANNO SI DICE (MANUALE §8.5: «nothing on screen says which year
            is being edited»): separatore §3.3, calco di «Manage Teams — 2026». */}
        <h2 className="qpsc-modal__title">{titolo}</h2>
        {annoLeggibile && (
          <p className="qpsc-modal__note uc-centered">
            Changes are saved to the {stagione} catalogue (the season you are viewing).
          </p>
        )}

        <div className="qpsc-modal__body">
          {offertaCopia ? (
            /* LA DOMANDA «copia dall'anno precedente» (decisione α del
               committente): compare SOLO alla prima apertura su un anno senza
               lista, se l'anno prima ne ha una. Nessuna delle due risposte
               scrive nulla: precompilano la lista, e poi si preme «Save». */
            <div className="uc-copy">
              <p className="qpsc-modal__note">
                This year's kit list is empty. Copy last year's ({annoPrecedente})?
              </p>
              <div className="uc-copy__actions">
                <button type="button" className="qpsc-tool-btn" onClick={copiaDallAnnoPrecedente}>
                  Copy
                </button>
                <button type="button" className="qpsc-ghost-btn" onClick={partiDalRipiego}>
                  Use the standard list
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* 1. LA LISTA DEI CAPI — calco di Manage Teams (`.ta-team-list`,
                  `.ta-team-row`, `.ta-team-form`): una riga per capo con la
                  label modificabile, le due spunte (taglia, acceso) e i due
                  comandi di riordino; in fondo il campo «New item» + «Add item».
                  Nessun «Delete», da nessuna parte. */}
              <section className="uc-section" aria-labelledby="uc-items-title">
                <h3 id="uc-items-title" className="uc-section__title">Items</h3>

                <div className="uc-item-list">
                  {capi.map((riga, indice) => (
                    <div
                      key={riga.chiave}
                      className={"uc-item" + (riga.active ? "" : " uc-item--off")}
                    >
                      <div className="qpsc-field uc-item__label">
                        <span className="qpsc-field__label">
                          {riga.active ? "Item" : "Item — off"}
                        </span>
                        <input
                          className="qpsc-input"
                          type="text"
                          aria-label={`Item name${riga.active ? "" : " (off)"}`}
                          value={riga.label}
                          onChange={(e) => rinomina(riga.chiave, e.target.value)}
                        />
                      </div>

                      <div className="uc-item__checks">
                        <label className="qpsc-check">
                          <input
                            className="qpsc-check__input"
                            type="checkbox"
                            aria-label={`${riga.label || "Item"} has a size`}
                            checked={riga.hasSize}
                            onChange={(e) => impostaTaglia(riga.chiave, e.target.checked)}
                          />
                          <span className="qpsc-check__box" aria-hidden="true" />
                          <span>Size</span>
                        </label>

                        <label className="qpsc-check">
                          <input
                            className="qpsc-check__input"
                            type="checkbox"
                            aria-label={`${riga.label || "Item"} is active`}
                            checked={riga.active}
                            onChange={(e) => impostaAttivo(riga.chiave, e.target.checked)}
                          />
                          <span className="qpsc-check__box" aria-hidden="true" />
                          <span>Active</span>
                        </label>
                      </div>

                      <div className="uc-item__move">
                        <button
                          type="button"
                          className="qpsc-ghost-btn"
                          aria-label={`Move ${riga.label || "item"} up`}
                          disabled={indice === 0}
                          onClick={() => sposta(indice, -1)}
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          className="qpsc-ghost-btn"
                          aria-label={`Move ${riga.label || "item"} down`}
                          disabled={indice === capi.length - 1}
                          onClick={() => sposta(indice, 1)}
                        >
                          ▼
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="uc-add">
                  <div className="qpsc-field">
                    <span className="qpsc-field__label">New item</span>
                    <input
                      className="qpsc-input"
                      type="text"
                      placeholder="New item name"
                      value={nuovaLabel}
                      onChange={(e) => setNuovaLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          aggiungi();
                        }
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    className="qpsc-tool-btn qpsc-tool-btn--wide"
                    disabled={!nuovaLabel.trim()}
                    onClick={aggiungi}
                  >
                    Add item
                  </button>
                </div>

                {conNuoviNonSalvati && (
                  <p className="qpsc-modal__note">{MSG_NUOVI}</p>
                )}
              </section>

              {/* 2. BY ROLE. ⚠️ Il nome di riga è la CHIAVE, stampata alla
                  lettera: il tutto-maiuscolo «COACH» è voluto (unica eccezione
                  al doc 07 §3.2 in questo lavoro) — un'etichetta leggibile
                  («Coach») renderebbe la riga canonica indistinguibile da una
                  riga storica «Coach». Una chiave che i lettori NON cercano
                  (non in RUOLI_CON_DIVISE) si dichiara «(not read)», in
                  inglese (CLAUDE.md §3.7: via l'italiano «(non letta)»). */}
              <section className="uc-section" aria-labelledby="uc-roles-title">
                <h3 id="uc-roles-title" className="uc-section__title">By Role</h3>
                {tabellaSpunte("roles", righeRuoli, "Role", (role) =>
                  RUOLI_CON_DIVISE.includes(role) ? null : (
                    <span className="uc-not-read"> (not read)</span>
                  )
                )}
              </section>

              {/* 3. BY TEAM — le righe sono le chiavi già nel catalogo (come
                  prima: questa modale non legge `/teams`). Separatore §3.3. */}
              <section className="uc-section" aria-labelledby="uc-teams-title">
                <h3 id="uc-teams-title" className="uc-section__title">
                  By Team — only for Players
                </h3>
                {tabellaSpunte("teams", Object.keys(config.teams || {}), "Team", null)}
              </section>
            </>
          )}
        </div>

        {/* L'esito dell'ultimo gesto, in modale: rosso leggibile per l'errore,
            secondario per la conferma (calco `.ta-alert` / `.ta-info`). */}
        {messaggio && (
          <p className={messaggio.tipo === "errore" ? "uc-alert" : "uc-info"} role="status">
            {messaggio.testo}
          </p>
        )}

        <div className="qpsc-modal__actions">
          <button
            type="button"
            className="qpsc-tool-btn"
            onClick={handleSave}
            disabled={saving || Boolean(offertaCopia)}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" className="qpsc-ghost-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UniformConfig;
